import { Base } from "@app/controllers/base"
import L from "leaflet"
import { addTileControl } from "./map_tile_control"
import "leaflet.markercluster"

delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconUrl:       new URL("leaflet/dist/images/marker-icon.png",    import.meta.url).href,
  iconRetinaUrl: new URL("leaflet/dist/images/marker-icon-2x.png", import.meta.url).href,
  shadowUrl:     new URL("leaflet/dist/images/marker-shadow.png",  import.meta.url).href,
})

export default class extends Base {
  static values = {
    venues: { type: Array,  default: [] },
    center: { type: Array,  default: [-8.4095, 115.1889] },
    zoom:   { type: Number, default: 10 },
  }

  static targets = ["mapContainer"]

  connect() {
    this._map          = null
    this._cluster      = null
    this._venueMarkers = {}
    this._dpMarkers    = {}
    this._activeBtn    = null
    this._initMap()
  }

  disconnect() {
    this._map?.remove()
    this._map = null
  }

  flyToVenue(event) {
    const btn   = event.currentTarget
    const id    = parseInt(btn.dataset.venueId, 10)
    const venue = this.venuesValue.find(v => v.id === id)
    if (!venue || !this._map) return

    this._setActive(btn)

    const pts = venue.dronePoints.map(dp => [dp.lat, dp.lon])
    pts.push(venue.center)
    if (pts.length >= 2) {
      this._map.flyToBounds(L.latLngBounds(pts), { padding: [60, 60], animate: true })
    } else {
      this._map.flyTo(venue.center, 15, { animate: true })
    }

    const vm = this._venueMarkers[id]
    if (vm) setTimeout(() => vm.openPopup(), 600)
  }

  flyToDronePoint(event) {
    const btn    = event.currentTarget
    const dpId   = parseInt(btn.dataset.dpId, 10)
    const marker = this._dpMarkers[dpId]
    if (!this._cluster || !marker) return

    this._setActive(btn)
    this._cluster.zoomToShowLayer(marker, () => marker.openPopup())
  }

  // ── private ─────────────────────────────────────────────────────────────────

  _initMap() {
    this._map = L.map(this.mapContainerTarget, {
      zoomControl: true,
      gestureHandling: true,
      maxZoom: 19,
    })
    this._map.setView(this.centerValue, this.zoomValue)

    this._cluster = L.markerClusterGroup({
      showCoverageOnHover: false,
      maxClusterRadius: 50,
      iconCreateFunction: this._clusterIcon,
    })

    this.venuesValue.forEach(venue => {
      const vm = L.marker(venue.center, { icon: this._venueIcon() })
        .bindPopup(this._venuePopup(venue), { maxWidth: 220 })
      vm.addTo(this._map)
      this._venueMarkers[venue.id] = vm

      venue.dronePoints.forEach(dp => {
        const marker = L.marker([dp.lat, dp.lon], { icon: this._droneIcon() })
          .bindPopup(this._dpPopup(dp, venue), { maxWidth: 200 })
        this._dpMarkers[dp.id] = marker
        this._cluster.addLayer(marker)
      })
    })

    this._map.addLayer(this._cluster)
    this._fitAll()

    const defaultTile = document.documentElement.getAttribute("data-bs-theme") === "dark" ||
                        document.documentElement.classList.contains("dark") ? "dark" : "street"
    addTileControl(this._map, defaultTile)

    requestAnimationFrame(() => this._map?.invalidateSize())
    setTimeout(() => this._map?.invalidateSize(), 150)
  }

  _fitAll() {
    const pts = []
    this.venuesValue.forEach(v => {
      pts.push(v.center)
      v.dronePoints.forEach(dp => pts.push([dp.lat, dp.lon]))
    })
    if (pts.length >= 2) {
      this._map.fitBounds(L.latLngBounds(pts), { padding: [48, 48] })
    } else if (pts.length === 1) {
      this._map.setView(pts[0], 14)
    } else {
      this._map.setView(this.centerValue, this.zoomValue)
    }
  }

  _setActive(btn) {
    this._activeBtn?.classList.remove("vms-item--active")
    this._activeBtn = btn
    btn.classList.add("vms-item--active")
    btn.scrollIntoView({ block: "nearest", behavior: "smooth" })
  }

  _venuePopup(venue) {
    return `
      <div style="font-size:13px;font-weight:700;color:#1f2a44;margin-bottom:2px">${venue.name}</div>
      ${venue.address ? `<div style="font-size:11px;color:#6b7280">${venue.address}</div>` : ""}
      <a href="${venue.url}" style="display:inline-block;margin-top:6px;font-size:11px;font-weight:600;color:#1f2a44;text-decoration:underline">View venue →</a>`
  }

  _dpPopup(dp, venue) {
    return `
      <div style="font-size:13px;font-weight:700;color:#1f2a44;margin-bottom:2px">${dp.name}</div>
      <div style="font-size:11px;color:#6b7280">${venue.name} · Launch Point</div>`
  }

  _clusterIcon(cluster) {
    const count = cluster.getChildCount()
    return L.divIcon({
      className: "",
      html: `<div style="
        width:36px;height:36px;border-radius:50%;
        background:#1f2a44;border:3px solid #fff;
        box-shadow:0 3px 10px rgba(0,0,0,.4);
        display:flex;align-items:center;justify-content:center;
        font-size:12px;font-weight:700;color:#fff;letter-spacing:-0.5px;
      ">${count}</div>`,
      iconSize: [36, 36], iconAnchor: [18, 18],
    })
  }

  _venueIcon() {
    return L.divIcon({
      className: "",
      html: `<div style="
        width:36px;height:36px;border-radius:50%;
        background:#c9a84c;border:3px solid #fff;
        box-shadow:0 3px 10px rgba(0,0,0,.4);
        display:flex;align-items:center;justify-content:center;
      "><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
        <circle cx="12" cy="10" r="3"/>
      </svg></div>`,
      iconSize: [36, 36], iconAnchor: [18, 36], popupAnchor: [0, -38],
    })
  }

  _droneIcon() {
    return L.divIcon({
      className: "",
      html: `<div style="
        width:28px;height:28px;border-radius:50%;
        background:#1f2a44;border:2.5px solid #fff;
        box-shadow:0 2px 8px rgba(0,0,0,.35);
        display:flex;align-items:center;justify-content:center;
      "><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
        <path d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/>
      </svg></div>`,
      iconSize: [28, 28], iconAnchor: [14, 14], popupAnchor: [0, -18],
    })
  }
}
